import { Select } from "@radix-ui/themes";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SelectPicker } from "rsuite";
import "rsuite/SelectPicker/styles/index.css";
import { UiAlert } from "../alert";

interface UiSelectProps {
  defaultValue?: string;
  value?: string;
  size?: "1" | "2" | "3";
  radius?: "none" | "large" | "full";
  options: { value: string; label: string }[];
  onChange?: (value: string) => void;
  className?: string;
  styles?: React.CSSProperties;
  placeholder?: string;
  label?: string;
  name?: string;
  errors?: string[];
  groupClassname?: string;
  appearance?: "subtle" | "default";
}
export function UiSelect({
  size = "3",
  radius = "large",
  defaultValue,
  options = [],
  onChange,
  className,
  styles,
  placeholder,
  appearance = "default",
  name,
  label,
  value,
  errors,
  groupClassname,
}: UiSelectProps) {
  return (
    <div className={groupClassname || ""}>
      {label && <label className="ui-form-label block">{label}</label>}
      <SelectPicker
        data={options}
        className={className}
        name={name}
        searchable={false}
        appearance={appearance}
        {...(value ? { value } : { defaultValue })}
        style={{ ...styles }}
        onChange={(v) => onChange?.(v || "")}
        size="lg"
        placeholder={placeholder || ""}
        aria-haspopup="listbox"
      />
      {errors && errors.length > 0 && (
        <UiAlert>
          {errors?.map((e) => (
            <p key={e}>{e}</p>
          ))}
        </UiAlert>
      )}
    </div>
  );
}

interface Option {
  value: string | number | null;
  label: string;
}

interface UiAdvancedSelectProps {
  options: Option[];
  onChange?: (value: string | null) => void;
  placeholder?: string;
  loading?: boolean;
  className?: string;
  label?: string;
  name?: string;
  defaultValue?: string | null;
  errors?: string[];
  styles?: any;
}

export function UiAdvancedSelect({
  options,
  placeholder = "Select an option",
  loading = false,
  className,
  label,
  name,
  defaultValue = null,
  errors = [],
  styles,
  onChange,
}: UiAdvancedSelectProps) {
  const [value, setValue] = useState<string | null>(defaultValue);

  // Memoize options transformation to prevent unnecessary re-renders
  const customOptions = useMemo(
    () =>
      options.map((option) => ({
        label: option.label,
        value: option.value != null ? String(option.value) : "",
      })),
    [options]
  );

  // Update value only when defaultValue changes
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  // Memoize handleChange to prevent unnecessary re-renders
  const handleChange = useCallback(
    (newValue: string | null) => {
      setValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  return (
    <div className="ui-advanced-select flex flex-col justify-start">
      {label && (
        <label className="ui-form-label" htmlFor={name}>
          {label}
        </label>
      )}
      <SelectPicker
        size="lg"
        className={className}
        loading={loading}
        data={customOptions}
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        block // Replaces style={{ width: "100%" }}
        searchable // Optional: enable search if needed
        menuStyle={{ ...styles }}
      />
      {errors.length > 0 && (
        <UiAlert className="ui-advanced-select__errors">
          {errors.map((error, index) => (
            <p key={`${error}-${index}`}>{error}</p>
          ))}
        </UiAlert>
      )}
    </div>
  );
}
